import { Component, OnInit, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CounselorNav } from './counselor-nav/counselor-nav';
import { UserAvatar } from '../../common/user-avatar/user-avatar';
import { HasPermissionDirective } from '../../../security/has-permission.directive';
import { USER_PERMISSIONS } from '../../../constants/permissions';
import { IssueReportsDialog } from '../district-view/district-reports/reports-dialog/issue-reports-dialog';
import {
  StudentService,
  CounselorStudent,
  GRADE_LABELS,
  deriveStatus,
} from '../../../services/student.service';

@Component({
  selector: 'app-conselor-view',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    CounselorNav,
    FormsModule,
    CommonModule,
    UserAvatar,
    HasPermissionDirective,
  ],
  templateUrl: './counselor-view.html',
  styleUrl: './counselor-view.css',
})
export class CounselorView implements OnInit {
  constructor(
    private title: Title,
    private studentService: StudentService,
  ) {}

  /** Permission constants exposed to the template. */
  permissions = USER_PERMISSIONS;

  /** Material dialog service used to open the create-report form. */
  private dialog = inject(MatDialog);

  /** Opens the issue-report creation dialog. */
  createReportDialog(): void {
    this.dialog.open(IssueReportsDialog, {
      width: '640px',
      maxWidth: '95vw',
      data: {},
      autoFocus: false,
    });
  }

  ngOnInit(): void {
    this.title.setTitle('Dashboard | Handle');
    this.studentService.getMyStudents().subscribe({
      next: (students) => {
        this.allStudents.set(students);
      },
      error: (err) => console.error(err),
    });
  }

  private allStudents = signal<CounselorStudent[]>([]);

  students = computed(() => this.allStudents());

  totalStudents = computed(() => this.allStudents().length);

  attentionCount = computed(
    () =>
      this.allStudents().filter(
        (s) => deriveStatus(s.Profile?.profile_complete_percent ?? 0) === 'needs-attention',
      ).length,
  );

  avgCompletion = computed(() => {
    const all = this.allStudents();
    if (!all.length) return 0;
    const sum = all.reduce((acc, s) => acc + (s.Profile?.profile_complete_percent ?? 0), 0);
    return Math.round(sum / all.length);
  });

  today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  deadlines = [
    { title: 'SAT May registration', date: 'Closes Apr 4', urgency: 'red' },
    { title: 'Spring internship fair', date: 'Apr 14 · Gym', urgency: 'amber' },
    { title: 'SAT June registration', date: 'Closes May 8', urgency: 'navy' },
    { title: 'ACT June registration', date: 'Closes May 9', urgency: 'navy' },
  ];

  initials(s: CounselorStudent): string {
    return `${(s.user.preferred_name || s.user.first_name)[0]}${s.user.last_name[0]}`.toUpperCase();
  }

  gradeLabel(level: number): string {
    return GRADE_LABELS[level] ?? `Grade ${level}`;
  }

  deriveStatus(s: CounselorStudent): string {
    return deriveStatus(s.Profile?.profile_complete_percent ?? 0);
  }

  statusLabel(s: CounselorStudent): string {
    const status = deriveStatus(s.Profile?.profile_complete_percent ?? 0);
    if (status === 'on-track') return 'On track';
    if (status === 'needs-attention') return 'Needs attention';
    return 'New';
  }

  avatarColor(s: CounselorStudent): string {
    const colors = ['#1E468B', '#3a6bbf', '#2a7a4f', '#1E468B', '#3a6bbf', '#1E468B'];
    return colors[s.id % colors.length];
  }

  addDeadline(): void {}
}
